import React from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import { Error as ErrorIcon, Refresh } from '@mui/icons-material';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px',
            p: 4,
            background: 'rgba(255, 255, 255, 0.02)',
            borderRadius: '16px',
            border: '1px solid rgba(255, 68, 68, 0.3)',
          }}
        >
          <ErrorIcon sx={{ fontSize: 48, color: '#ff4444', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#ff4444', mb: 1 }}>
            Something went wrong
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3, textAlign: 'center' }}>
            A component encountered an error. You can try refreshing the page or contact support if the problem persists.
          </Typography>

          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={this.handleRetry}
            sx={{
              borderColor: 'rgba(255, 68, 68, 0.5)',
              color: '#ff4444',
              '&:hover': {
                borderColor: '#ff4444',
                backgroundColor: 'rgba(255, 68, 68, 0.1)',
              },
            }}
          >
            Try Again
          </Button>

          {/* Always show error details for debugging */}
          {this.state.error && (
            <Alert severity="error" sx={{ mt: 3, width: '100%' }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', mb: 1 }}>
                {this.state.error.toString()}
              </Typography>
              {this.state.errorInfo && (
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', whiteSpace: 'pre-wrap' }}>
                  {this.state.errorInfo.componentStack}
                </Typography>
              )}
            </Alert>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;